import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import verifyProfilesVision from "utils/verifyProfilesVision";
import ScrollBar from "react-perfect-scrollbar";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import LoadingData from "components/Loading/LoadingData";
import { LONG_STATUS_SOLICITACAO } from "./table-requests";
import ConfirmDialog from "./confirm-dialog";
import DialogGalery from "./dialog-galery";
import {
  Close,
  HideImageOutlined,
  KeyboardDoubleArrowRight,
  Photo,
  Replay,
} from "@mui/icons-material";
import DialogSendDocument from "./dialog-send-document";
import mapHistoricoDevolucaoTimelineEntry from "utils/mapHistoricoDevolucaoTimelineEntry";

export default function ViewDialog({ onClose, idSolicitacao }) {
  const [details, setDetails] = useState();
  const [searching, setSearching] = useState(false);
  const [steps, setSteps] = useState([]);
  const [showingPhoto, setShowingPhoto] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
  const [openSendDocumentDialog, setOpenSendDocumentDialog] = useState(false);
  const [filterNotaFiscal, setFilterNotaFiscal] = useState("");
  const snakebar = useSnackbar();

  const isADM = verifyProfilesVision(["CRC", "ADM"]);
  const isOpen = details?.StatusSolicitacao === "ABERTA";
  const isWaitingDocuments = details?.StatusSolicitacao === "AG_ENVIO_NOTA";
  const isDocValidation = details?.StatusSolicitacao === "DOC_EM_VALIDACAO";
  const isReturned = details?.StatusSolicitacao === "REPROVADO";
  const isDocReproved = details?.StatusSolicitacao === "DOC_REPROVADO";

  const handleSearchDetails = async () => {
    setSearching(true);

    try {
      const response = await Api.get(
        "list-request-management-reason",
        "returnRequest",
        "?idSolicitacao=" + idSolicitacao,
        true,
      );
      const resultado = response?.Result;
      setDetails(resultado);

      if (resultado?.HistoricoAlteracoes) {
        const chronologicalHistory = [...resultado.HistoricoAlteracoes].sort(
          (a, b) =>
            a.IdSolicitacaoDevolucaoHistorico -
            b.IdSolicitacaoDevolucaoHistorico,
        );

        const mappedSteps = chronologicalHistory.map((item) => {
          const { description, icon } = mapHistoricoDevolucaoTimelineEntry(
            item,
            { solicitacao: resultado },
          );

          return {
            date: item.DataAlteracao,
            description: description,
            icon: icon,
          };
        });

        setSteps(mappedSteps);
      }
    } catch (error) {
      snakebar.showMessage(
        "Erro ao buscar detalhes da solicitação de devolução: " + error.message,
        {
          severity: "error",
        },
      );
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    handleSearchDetails();
  }, []);

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="xl">
        <DialogTitle>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h3" color="primary">
              <Replay style={{ marginRight: "12px" }} />
              Visualizar Solicitação
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          {searching ? (
            <Grid item xs={12}>
              <LoadingData searching label="Buscando detalhes..." />
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={1}>
                <Typography variant="h4" color="primary" align="center">
                  Código
                </Typography>
                <Typography variant="caption" color="primary" align="center">
                  {details?.IdSolicitacao}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <Typography variant="h4" color="primary" align="center">
                  Status
                </Typography>
                <Typography variant="caption" color="primary" align="center">
                  {LONG_STATUS_SOLICITACAO[details?.StatusSolicitacao]}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="h4" color="primary">
                  CNPJ
                </Typography>
                <Typography variant="caption" color="primary">
                  {MaskAndValid.maskCNPJ(details?.CNPJ || "")}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="h4" color="primary">
                  Loja
                </Typography>
                <Typography variant="caption" color="primary">
                  {details?.Empresa}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="h4" color="primary">
                  Motivo Devolução
                </Typography>
                <Typography variant="caption" color="primary">
                  {details?.Motivo}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="h4" color="primary">
                  Defeito
                </Typography>
                <Typography variant="caption" color="primary">
                  {details?.Defeito}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="h4" color="primary">
                  Data da Solicitação
                </Typography>
                <Typography variant="caption" color="primary" align="center">
                  {MaskAndValid.formattDateBR(details?.DataCriado)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "16px",
                  }}
                >
                  <Typography variant="h4" color="primary">
                    Produtos
                  </Typography>
                  <TextField
                    label="Filtrar por Nota Fiscal"
                    variant="outlined"
                    size="small"
                    value={filterNotaFiscal}
                    onChange={(e) => setFilterNotaFiscal(e.target.value)}
                    style={{ width: "250px" }}
                  />
                </div>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Código Produto</TableCell>
                        <TableCell>Descrição Produto</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Saldo</TableCell>
                        <TableCell>Qtd Devolução</TableCell>
                        <TableCell>Nota Fiscal</TableCell>
                        <TableCell>Serie</TableCell>
                        <TableCell>Data Emissão</TableCell>
                        <TableCell>Evidências</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details?.Itens?.filter((item) => {
                        if (!filterNotaFiscal) return true;
                        return item.NumeroNota?.toString()
                          .toLowerCase()
                          .includes(filterNotaFiscal.toLowerCase());
                      }).map((item) => {
                        const hasImages =
                          item.Imagens && item.Imagens.length > 0;
                        return (
                          <TableRow key={item.Id}>
                            <TableCell align="center">
                              {item.IdProduto}
                            </TableCell>
                            <TableCell>{item.NomeProduto}</TableCell>
                            <TableCell align="center">{item.Total}</TableCell>
                            <TableCell align="center">
                              {item.QuantidadeNF}
                            </TableCell>
                            <TableCell align="center">
                              {item.Quantidade}
                            </TableCell>
                            <TableCell align="center">
                              {item.NumeroNota}
                            </TableCell>
                            <TableCell align="center">{item.Serie}</TableCell>
                            <TableCell align="center">
                              {item.DataEmissaoFormat}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  hasImages
                                    ? "Visualizar imagens"
                                    : "Não há imagens"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    hasImages
                                      ? setShowingPhoto(item.Imagens)
                                      : null
                                  }
                                >
                                  {hasImages ? (
                                    <Photo fontSize="small" />
                                  ) : (
                                    <HideImageOutlined fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {(details?.VolumePesoLiquido != null ||
                (details?.Volumes?.length ?? 0) > 0) && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h4" color="primary">
                      Informações de Volumes
                    </Typography>
                    <Grid container spacing={2} style={{ marginTop: "0.5rem" }}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          Peso líquido
                        </Typography>
                        <Typography variant="body1">
                          {details?.VolumePesoLiquido != null
                            ? Number(details.VolumePesoLiquido).toLocaleString(
                                "pt-BR",
                              )
                            : "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                    {(details?.Volumes?.length ?? 0) > 0 && (
                      <>
                        <TableContainer style={{ marginTop: "1rem" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Espécie</TableCell>
                                <TableCell align="right">
                                  Quantidade volume
                                </TableCell>
                                <TableCell align="right">Peso bruto</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {details.Volumes.map((row) => (
                                <TableRow key={row.IdVolume ?? row.Especie}>
                                  <TableCell>{row.Especie ?? "-"}</TableCell>
                                  <TableCell align="right">
                                    {row.QuantidadeVolume ?? "-"}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.PesoBrutoFormat ??
                                      (row.PesoBruto != null
                                        ? Number(row.PesoBruto).toLocaleString(
                                            "pt-BR",
                                          )
                                        : "-")}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Typography
                          variant="body1"
                          style={{ marginTop: "0.5rem" }}
                        >
                          Total peso bruto:{" "}
                          {details.VolumePesoBruto != null
                            ? Number(details.VolumePesoBruto).toLocaleString(
                                "pt-BR",
                              )
                            : details.Volumes.reduce(
                                (s, v) => s + (Number(v.PesoBruto) || 0),
                                0,
                              ).toLocaleString("pt-BR")}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h4" color="primary">
                  Histórico de Movimentação
                </Typography>
                <ScrollBar>
                  <Grid
                    container
                    alignItems="center"
                    style={{ margin: "2rem 0", flexWrap: "nowrap" }}
                  >
                    {steps?.map((step, index) => (
                      <>
                        {index > 0 && <KeyboardDoubleArrowRight />}
                        <Grid
                          item
                          alignItems="center"
                          key={index}
                          style={{ width: "200px", padding: "0 1rem" }}
                        >
                          {step.icon}
                          <Typography variant="caption">
                            {MaskAndValid.formattDateBR(step.date)}
                          </Typography>
                          <Typography variant="caption" align="center">
                            {step.description}
                          </Typography>
                        </Grid>
                      </>
                    ))}
                  </Grid>
                </ScrollBar>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Grid
            container
            spacing={2}
            style={{ justifyContent: "flex-end", gap: "1rem" }}
          >
            {isADM && (
              <>
                {isOpen && (
                  <Grid item xs={6} lg={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        setOpenConfirmDialog(isOpen ? "accept" : "acceptDoc")
                      }
                    >
                      Aprovar Solicitação
                    </Button>
                  </Grid>
                )}
                {(isOpen || isDocReproved) && (
                  <Grid item xs={6} lg={3}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() =>
                        setOpenConfirmDialog(isOpen ? "reject" : "rejectDoc")
                      }
                    >
                      {isOpen ? "Rejeitar Solicitação" : "Rejeitar Documento"}
                    </Button>
                  </Grid>
                )}

                {isWaitingDocuments && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenSendDocumentDialog(true)}
                  >
                    Upload de documentos
                  </Button>
                )}

                {(isDocValidation || isWaitingDocuments) && (
                  <Button variant="outlined" color="primary" onClick={onClose}>
                    Fechar
                  </Button>
                )}

                {(isReturned || isDocValidation) && (
                  <Button variant="contained" color="primary" onClick={onClose}>
                    Voltar
                  </Button>
                )}
              </>
            )}
            {!isADM && (
              <>
                {(isWaitingDocuments || isDocReproved) && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenSendDocumentDialog(true)}
                  >
                    Upload de documentos
                  </Button>
                )}
                <Button variant="outlined" color="primary" onClick={onClose}>
                  Fechar
                </Button>
              </>
            )}
          </Grid>
        </DialogActions>
      </Dialog>

      {openConfirmDialog && (
        <ConfirmDialog
          type={openConfirmDialog}
          onClose={() => setOpenConfirmDialog(null)}
          idSolicitacao={idSolicitacao}
          handleSearch={handleSearchDetails}
        />
      )}

      {showingPhoto && (
        <DialogGalery
          onClose={() => setShowingPhoto(null)}
          images={showingPhoto}
        />
      )}

      {openSendDocumentDialog && (
        <DialogSendDocument
          onClose={() => setOpenSendDocumentDialog(null)}
          idSolicitacao={idSolicitacao}
          handleSearch={handleSearchDetails}
        />
      )}
    </>
  );
}
